import {ReactElement, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Toolbar,
  Typography,
} from '@mui/material';

import {EmpionLogoWithText} from '../EmpionLogoWithText';

import {greenColors} from '../../../layouts/theme/designSystem';
import {AppRouteResource} from '../../../types/AppRouteResource';
import {EmpionTooltip} from '../../EmpionTooltip/EmpionTooltip';

type MenuItem = AppRouteResource | ReactElement;

const isAppRouteResource = (item: MenuItem): item is AppRouteResource => !!(item as AppRouteResource)?.navigationPath;

interface SidebarMenuGroup {
  title?: string;
  menuItems: MenuItem[];
}

interface SidebarSection {
  title?: string;
  menuItems?: MenuItem[];
  groups?: SidebarMenuGroup[];
  isDisabled?: boolean;
  disabledText?: string;
}

interface Props {
  sideBarSections: SidebarSection[];
}

export const SideDrawerWrapper = ({sideBarSections}: Props) => {
  const {t} = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const openDrawer = () => {
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onClickHandler = (path: string) => {
    closeDrawer();
    navigate(path);
  };

  return (
    <AppBar
      position='sticky'
      sx={{
        backgroundColor: '#fff',
        backgroundImage: 'none',
        boxShadow: 'none',
        zIndex: 1000,
        mb: '50px',
      }}
    >
      <Container maxWidth='lg'>
        <Toolbar disableGutters sx={{justifyContent: 'space-between'}}>
          <Box
            width='100%'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}
          >
            <Grid container justifyContent='flex-start' flexWrap='nowrap' alignItems='center'>
              <IconButton
                size='large'
                aria-label='account of current user'
                aria-controls='menu-appbar'
                aria-haspopup='true'
                onClick={openDrawer}
                sx={{paddingLeft: 0, color: greenColors[900]}}
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <EmpionLogoWithText />
          </Box>
        </Toolbar>
        <Drawer
          anchor='left'
          open={isDrawerOpen}
          onClose={closeDrawer}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <List sx={{flex: 1, backgroundColor: 'background.default'}}>
            {sideBarSections.map((section, idx) => (
              <Box key={section.title || `section-${idx}`}>
                <Box my={1}>
                  {section.title && (
                    <Typography variant='h6' sx={{mb: 1, ml: 2}}>
                      {t(section.title)}
                    </Typography>
                  )}
                  {(section.groups ?? [{menuItems: section.menuItems ?? []}]).map((group, groupIdx) => (
                    <Box key={group.title || `group-${groupIdx}`}>
                      {group.title && (
                        <Typography
                          variant='subtitle2'
                          sx={{mb: 1, ml: 2, fontWeight: 700, textTransform: 'uppercase'}}
                        >
                          {t(group.title)}
                        </Typography>
                      )}
                      {group.menuItems.map((item) => {
                        if (isAppRouteResource(item)) {
                          return (
                            <EmpionTooltip
                              key={item.label}
                              title={section.disabledText || ''}
                              isEnabled={section.isDisabled}
                            >
                              <ListItemButton
                                component='a'
                                disabled={section.isDisabled}
                                onClick={() => onClickHandler(item.navigationPath)}
                              >
                                <ListItemIcon
                                  sx={{
                                    marginRight: '10px',
                                    color: greenColors[900],
                                  }}
                                >
                                  {item.icon}
                                </ListItemIcon>
                                <Typography>{t(item.label)}</Typography>
                              </ListItemButton>
                            </EmpionTooltip>
                          );
                        }

                        return item;
                      })}
                    </Box>
                  ))}
                </Box>
                <Divider />
              </Box>
            ))}
          </List>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'background.default',
              p: 1,
            }}
          >
            <IconButton onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Drawer>
      </Container>
    </AppBar>
  );
};
